import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { navItems, profile } from "../data/mock";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const hrefFor = (anchor) => (onHome ? anchor : `/${anchor}`);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#07061A]/70 border-b border-white/5"
          : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <a
          href={onHome ? "#top" : "/"}
          className="group flex items-center gap-2"
          data-testid="navbar-logo"
        >
          <span className="relative inline-flex h-2.5 w-2.5">
            <span className="absolute inset-0 rounded-full bg-gradient-to-br from-violet-400 via-fuchsia-400 to-cyan-400 animate-pulse-soft" />
            <span className="absolute inset-0 rounded-full bg-gradient-to-br from-violet-400 via-fuchsia-400 to-cyan-400 blur-md opacity-70" />
          </span>
          <span className="font-display text-sm font-semibold tracking-tight text-white">
            {profile.shortName}
            <span className="text-gradient-aurora">.</span>
          </span>
          <span className="hidden sm:inline font-mono text-[11px] text-cyan-300/60 uppercase tracking-widest">
            / ai-eng
          </span>
        </a>

        <ul className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <li key={item.href}>
              <a
                href={hrefFor(item.href)}
                className="px-3 py-2 text-sm text-cyan-100/70 hover:text-white transition-colors duration-200"
                data-testid={`nav-link-${item.label.toLowerCase()}`}
              >
                {item.label}
              </a>
            </li>
          ))}
        </ul>

        <a
          href={profile.resumeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden md:inline-flex items-center gap-1.5 text-sm font-medium text-white px-4 py-2 rounded-full bg-gradient-to-r from-cyan-400 via-teal-400 to-sky-500 hover:from-cyan-300 hover:via-teal-300 hover:to-sky-400 transition-[background] duration-300 glow-violet"
          data-testid="navbar-resume-btn"
        >
          Resume
          <ArrowUpRight className="h-3.5 w-3.5" />
        </a>

        <button
          className="md:hidden p-2 text-white"
          onClick={() => setOpen((s) => !s)}
          aria-label="Toggle menu"
          data-testid="navbar-mobile-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden border-t border-white/5 bg-[#07061A]/90 backdrop-blur-xl"
            data-testid="navbar-mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={hrefFor(item.href)}
                  onClick={() => setOpen(false)}
                  className="py-2 text-base text-cyan-100/90 hover:text-white"
                  data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </a>
              ))}
              <a
                href={profile.resumeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center justify-center gap-1.5 text-sm font-medium text-white px-4 py-2.5 rounded-full bg-gradient-to-r from-cyan-400 via-teal-400 to-sky-500"
                data-testid="mobile-resume-btn"
              >
                Resume <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
